/**
 * Inpakken detail: één webshop-order afvinken en markeren als ingepakt.
 * Werkbank-render (fase 2): adres als feiten, items als rijen met vinkje
 * (groen = gecontroleerd), "Ingepakt" vast onderin. Payload ongewijzigd.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import clsx from 'clsx';
import { useInpakkenStore, useHeader } from '../stores';
import { WbButton, WbFactRow, WbFlag, WbListRow, WbSectionKop, WbState, StickyActionBar } from '../components/wb';

function formatPrice(value: number | null): string {
  if (value == null) return '';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(value);
}

export function InpakkenDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const order = useInpakkenStore((s) => (orderId ? s.getOrderById(orderId) : undefined));
  const { fetchOrders, removeOrder, restoreOrder, markPacked } = useInpakkenStore();

  const [isFetching, setIsFetching] = useState(false);
  const [isPacking, setIsPacking] = useState(false);
  const [packError, setPackError] = useState<string | null>(null);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [skipChecklist, setSkipChecklist] = useState(false);

  // If order not in store (eg. direct deep-link), fetch list to populate.
  useEffect(() => {
    if (!order && orderId) {
      setIsFetching(true);
      fetchOrders().finally(() => setIsFetching(false));
    }
  }, [order, orderId, fetchOrders]);

  const allChecked = useMemo(() => {
    if (!order || order.items.length === 0) return true;
    return order.items.every((i) => checkedItems.has(i.itemId));
  }, [order, checkedItems]);

  const canPack = skipChecklist || allChecked;

  useHeader(order ? order.orderName : 'Inpakken', order ? `${checkedItems.size}/${order.items.length}` : null);

  function toggleItem(itemId: string) {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }

  function handleSkipChecklist() {
    if (!order) return;
    setSkipChecklist(true);
    setCheckedItems(new Set(order.items.map((i) => i.itemId)));
  }

  async function handlePack() {
    if (!order || !orderId || isPacking) return;

    setIsPacking(true);
    setPackError(null);

    const removed = removeOrder(orderId);

    const result = await markPacked(orderId);

    if (result.success) {
      navigate('/inpakken');
      return;
    }

    if (removed) restoreOrder(removed);
    setPackError(result.message || 'Kon order niet markeren als ingepakt');
    setIsPacking(false);
  }

  if (isFetching && !order) {
    return <WbState variant="laden" titel="Order laden…" />;
  }

  if (!order) {
    return (
      <div className="space-y-3">
        <Link to="/inpakken" className="wb-focus inline-flex items-center min-h-tap font-semibold underline underline-offset-4 text-wb-inkt">
          ‹ Terug naar lijst
        </Link>
        <WbState variant="leeg" titel="Order niet gevonden." tekst="Deze order is mogelijk al ingepakt of bestaat niet." />
      </div>
    );
  }

  const plaats = [order.postalCode, order.city].filter(Boolean).join(' ');

  return (
    <div className="space-y-5 pb-[140px]">
      <Link to="/inpakken" className="wb-focus inline-flex items-center gap-1 min-h-tap font-mono text-xl font-semibold text-wb-inkt">
        <span aria-hidden>‹</span> {order.orderName}
      </Link>

      {/* Verzendadres als feiten */}
      <WbFactRow
        facts={[
          { label: 'Klant', value: order.customerName || 'Onbekende klant', tekst: true },
          { label: 'Adres', value: [order.street, plaats, order.country].filter(Boolean).join(', ') || '–', tekst: true },
        ]}
      />
      {order.trackingNumber && <p className="font-mono text-sm text-wb-inkt2 -mt-3 break-all">Tracking {order.trackingNumber}</p>}

      {order.notes && <WbFlag kind="opmerking" title="Notitie">{order.notes}</WbFlag>}

      {/* Items: tik = gecontroleerd (groen) */}
      <section className="space-y-1.5">
        <WbSectionKop label="Items" teller={`${checkedItems.size}/${order.items.length}`} />
        {order.items.map((item) => {
          const checked = checkedItems.has(item.itemId);
          const meta = [
            item.quantity != null ? `${item.quantity}×` : null,
            item.price != null ? formatPrice(item.price) : null,
            item.width != null && item.length != null ? `${item.length} × ${item.width} cm` : null,
          ].filter(Boolean).join(' · ');
          return (
            <WbListRow
              key={item.itemId}
              onClick={() => toggleItem(item.itemId)}
              klaar={checked}
              tekst
              hoofd={item.productName || 'Onbekend product'}
              sub={meta || undefined}
              aria-label={`${checked ? 'Gecontroleerd' : 'Nog controleren'}: ${item.productName ?? ''}`}
              links={
                <span
                  aria-hidden
                  className={clsx(
                    'w-8 h-8 shrink-0 rounded-[4px] border-wb flex items-center justify-center',
                    checked ? 'bg-wb-inkt border-wb-inkt text-white' : 'border-wb-inkt3 bg-white text-transparent'
                  )}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              }
              rechts={null}
            />
          );
        })}
      </section>

      {packError && <WbFlag kind="geld" title="Niet gelukt">{packError}</WbFlag>}

      <StickyActionBar>
        {!skipChecklist && !allChecked && (
          <WbButton variant="link" onClick={handleSkipChecklist}>Checklist overslaan</WbButton>
        )}
        <WbButton size="lg" onClick={handlePack} disabled={!canPack} isLoading={isPacking}>
          Ingepakt
        </WbButton>
      </StickyActionBar>
    </div>
  );
}
